import React from 'react';
import * as Yup from 'yup';
import { toPattern } from 'vanilla-masker';
import { useNavigation} from 'hooks/index';
import { BizonSidebar, Button, Title, Input } from 'components/commons';
import { isValidCPF } from '@brazilian-utils/brazilian-utils';
import { InvalidApplicantReason } from 'generated/credit-analysis-graphql';
import { useApplicantHandler } from './hooks'
import { Field, Form } from 'components/form-validation';
import Modal from 'components/commons/modal';
import './bizon-approved-partner.styles.scss';
import whatsapp from 'assets/svg/whatsapp-green.svg';
import armoredIcon from 'assets/svg/armored-icon.svg';
import huggyIcon from 'assets/img/hurry-up.png';
import { getLoanApplicationParams } from 'utils/utils';
import { useGetLoanQuery } from 'generated/graphql';

type PartnerInfo = {
    document: string,
};

const getFormattedCompanyDocument = (companyDocument: string) => toPattern(companyDocument, '99.999.999/9999-99');

const ApprovedPartner: React.FC = (): JSX.Element => {
    const loanApplicationId  = getLoanApplicationParams();
    const [applicantNotFoundError, setApplicantNotFoundError] = React.useState(false);
    const [applicantExistsError, setApplicantExistsError] = React.useState(false);
    const [formLoading, setFormLoading] = React.useState(false);

    const { data } = useGetLoanQuery({
        variables:{ id: loanApplicationId},
        fetchPolicy: "network-only"
    });

    React.useEffect(() => {
        if (applicantExistsError) {
            partnerSchema().isValidSync('');
        }
    }, [applicantExistsError]);

    const {
        goToPartnerAntiFraudStatus,
        goToError,
    } = useNavigation();

    const setAntiFraudApplicant = useApplicantHandler();

    const partnerSchema = () => {
        let message = 'CPF inválido';
        let validator = isValidCPF;

        if (applicantExistsError) {
            message = 'Tente o CPF que você inseriu pela primeira vez';
            validator = () => false;
        }

        return Yup.object().shape<PartnerInfo>({
            document: Yup.string().test('', message, validator),
        });
    };

    const submit = async ({ document }: PartnerInfo) => {
        try {
            setFormLoading(true);
            const {
                data
            } = await setAntiFraudApplicant({
                variables: {
                    applicantInput: {
                        loanApplicationId,
                        document,
                    },
                }
            });

            if (data.setAntiFraudApplicantForLoanApplication.__typename === 'Applicant') {
                goToPartnerAntiFraudStatus();
                return;
            }

            setFormLoading(false);
            switch (data.setAntiFraudApplicantForLoanApplication.reason) {
                case InvalidApplicantReason.InvalidDocument:
                case InvalidApplicantReason.NotFound:
                    setApplicantNotFoundError(true);
                    break;
                case InvalidApplicantReason.AlreadyExists:
                    setApplicantExistsError(true);
                    break;
            }
        } catch {
            goToError();
        }
    };

    return (
        <>
            <div className="bizon-approved-partner">
                <Form
                    schema={partnerSchema()}
                    initialValues={{
                        document: '',
                    }}
                    onSubmit={submit}
                    render={({ valid }) => (
                        <>
                            <div className="bizon-approved-partner__content">
                                <div className="bizon-approved-partner__content-colunm">
                                    <Title className="bizon-approved-partner__title">olá! você pediu um empréstimo na Biz</Title>
                                    <span className="bizon-approved-partner__text">
                                        Para iniciar o processo precisamos que você insira o seu CPF.
                                        <br />
                                        <i>Empréstimo pedido para CNPJ: {data?.loanApplication &&
                                            getFormattedCompanyDocument(data?.loanApplication?.borrower?.companyDocument)}</i>
                                    </span>
                                    <Field
                                        onFocus={() => {
                                            setApplicantExistsError(false);
                                        }}
                                        component={Input}
                                        placeholder="CPF"
                                        name="document"
                                        mask="cpf"
                                    />
                                    <div className="bizon-approved-partner__message">
                                        <img
                                            className="bizon-invalid-partner__icon" src={armoredIcon}
                                        />
                                        <p>Fique tranquilo! Nós usamos essas informações apenas para a aprovação do seu empréstimo.</p>
                                    </div>
                                </div>
                                <div className="bizon-approved-partner__content-colunm">
                                    <div className="bizon-approved-partner__time">
                                        <img
                                            className="bizon-invalid-partner__icon" src={huggyIcon}
                                        />
                                        <p>falta pouco! não deixe sua oferta pré-aprovada expirar</p>
                                    </div>
                                    <BizonSidebar
                                        initialClosed={true}
                                        className="bizon-checkout__sidebar"
                                        boxTitle={<>caso tenha dúvidas, entre em contato pelos canais abaixo:</>}
                                    />
                                </div>
                            </div>
                            <div className="bizon-approved-partner__footer">
                                <div>
                                    <Button
                                        type="submit"
                                        label="vamos começar"
                                        disabled={!valid}
                                        loading={formLoading}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                />
            </div>
            <Modal
                title=""
                visible={applicantNotFoundError}
                onClose={() => setApplicantNotFoundError(false)}
            >
                <Title className="bizon-approved-partner__title">ops! não encontramos o registro deste sócio</Title>
                <p>
                    Talvez este CPF ainda não esteja registrado como sócio desta empresa.
                    <br /><br />
                    Precisa de ajuda? <a target="_blank" href="https://wa.me/5521970362474">Fale conosco <img className="svg-wp" src={whatsapp} role='presentation'/></a>
                </p>
                <Button
                    type="button"
                    label="tente novamente"
                    onClick={() => { setApplicantNotFoundError(false) }}
                />
            </Modal>
        </>
    );
};

export default ApprovedPartner;
